import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';

/* ─── Animations ──────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const bounceSubtle = keyframes`
  0%, 100% { transform: translateY(0) translateX(-50%); }
  50%       { transform: translateY(10px) translateX(-50%); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: scale(1.06); }
  to   { opacity: 1; transform: scale(1); }
`;

const kenBurns = keyframes`
  from { transform: scale(1); }
  to   { transform: scale(1.08); }
`;

const progressAnim = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

/* ─── Slides data ─────────────────────────────────────────── */
const slides = [
  {
    image: '/images/doscajetasunataza.jpeg',
    eyebrow: { en: 'San Juan de los Lagos, Jalisco · Since 1950', es: 'San Juan de los Lagos, Jalisco · Desde 1950', fr: 'San Juan de los Lagos, Jalisco · Depuis 1950' },
    title:   { en: 'A Tradition in Every Spoonful.', es: 'Una Tradición en Cada Cucharada.', fr: 'Une Tradition à Chaque Cuillère.' },
    body:    {
      en: "Handcrafted Mexican caramel made with 100% cow's milk. No preservatives. Pure heritage.",
      es: 'Cajeta artesanal mexicana hecha con 100% leche de vaca. Sin conservadores. Puro sabor de origen.',
      fr: "Caramel mexicain artisanal fait avec 100 % de lait de vache. Sans conservateurs. Un pur héritage.",
    },
    primary:   { en: 'Shop Now',    es: 'Compra Ahora',     fr: 'Acheter',         to: '/store'  },
    secondary: { en: 'Our Story',   es: 'Nuestra Historia', fr: 'Notre Histoire',  to: '/about'  },
    overlay: 'rgba(26,18,8,0.25) 0%, rgba(26,18,8,0.52) 55%, rgba(26,18,8,0.7) 100%',
  },
  {
    image: '/images/hero-cajeta.jpg',
    eyebrow: { en: 'Wholesale & Private Label', es: 'Mayoreo y Marca Propia', fr: 'Vente en Gros & Marque Privée' },
    title:   { en: 'Scale with the Best.', es: 'Crece con los Mejores.', fr: 'Grandissez avec les Meilleurs.' },
    body:    {
      en: 'From 700 g jars to full pallets. FDA, COFEPRIS & HACCP certified. We produce under your brand.',
      es: 'Desde frascos de 700 g hasta tarimas completas. Certificados FDA, COFEPRIS y HACCP. Producimos bajo tu marca.',
      fr: 'De pots de 700 g à des palettes entières. Certifiés FDA, COFEPRIS & HACCP. Nous produisons sous votre marque.',
    },
    primary:   { en: 'Request a Quote', es: 'Solicitar Cotización', fr: 'Demander un Devis', to: '/contact' },
    secondary: { en: 'See Products',    es: 'Ver Productos',        fr: 'Voir les Produits', to: '/store'   },
    overlay: 'rgba(26,18,8,0.35) 0%, rgba(124,58,0,0.55) 55%, rgba(26,18,8,0.72) 100%',
    badge:   { en: '🏆  PrimusGFS · FDA · HACCP Certified', es: '🏆  Certificado PrimusGFS · FDA · HACCP', fr: '🏆  Certifié PrimusGFS · FDA · HACCP' },
  },
  {
    image: '/images/doscajetasunataza.jpeg',
    eyebrow: { en: 'Now Available Online', es: 'Ahora Disponible en Línea', fr: 'Maintenant Disponible en Ligne' },
    title:   { en: 'Order Direct.\nShip Nationwide.', es: 'Pide Directo.\nEnvío a Todo el País.', fr: 'Commandez Directement.\nLivraison Partout.' },
    body:    {
      en: 'Shop our cajeta on TikTok Shop, Walmart, Bodega Aurrerá and Mercado Libre. Delivered to your door.',
      es: 'Compra nuestra cajeta en TikTok Shop, Walmart, Bodega Aurrerá y Mercado Libre. Directo a tu puerta.',
      fr: 'Achetez notre cajeta sur TikTok Shop, Walmart, Bodega Aurrerá et Mercado Libre. Livré chez vous.',
    },
    primary:   { en: 'Where to Buy', es: 'Dónde Comprar', fr: 'Où Acheter',    to: '/store'   },
    secondary: { en: 'Contact Us',   es: 'Contáctanos',   fr: 'Contactez-nous', to: '/contact' },
    overlay: 'rgba(26,18,8,0.3) 0%, rgba(26,18,8,0.58) 55%, rgba(26,18,8,0.72) 100%',
    stores: ['TikTok Shop', 'Walmart', 'Bodega Aurrerá', 'Mercado Libre'],
  },
];

/* ─── Styled components ───────────────────────────────────── */
const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SlideWrap = styled.div`
  position: absolute;
  inset: 0;
  animation: ${slideIn} 1.1s cubic-bezier(0.16,1,0.3,1) both;
`;

const BgImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  animation: ${kenBurns} 7s ease-out both;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, ${({ $grad }) => $grad});
`;

const GrainOverlay = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.04;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  background-size: 200px 200px;
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  max-width: 860px;
  margin: 0 auto;
  padding: 0 32px;
  text-align: center;
  color: #FFFDF7;
`;

const Eyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255,253,247,0.75);
  margin-bottom: 20px;
  animation: ${fadeUp} 0.7s 0.2s ease both;
`;

const Title = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(3rem, 9vw, 6rem);
  font-weight: 600;
  line-height: 1.05;
  margin-bottom: 28px;
  white-space: pre-line;
  animation: ${fadeUp} 0.8s 0.35s ease both;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  font-size: clamp(0.95rem, 2.2vw, 1.15rem);
  font-weight: 300;
  line-height: 1.75;
  max-width: 520px;
  margin: 0 auto 20px;
  color: rgba(255,253,247,0.85);
  animation: ${fadeUp} 0.8s 0.5s ease both;
`;

const Badge = styled.div`
  display: inline-block;
  background: rgba(192,112,32,0.22);
  border: 1px solid rgba(192,112,32,0.45);
  backdrop-filter: blur(8px);
  padding: 7px 18px;
  border-radius: 999px;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  color: rgba(255,253,247,0.92);
  margin-bottom: 24px;
  animation: ${fadeUp} 0.8s 0.6s ease both;
`;

const StoreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 24px;
  animation: ${fadeUp} 0.8s 0.6s ease both;
`;

const StorePill = styled.span`
  background: rgba(255,253,247,0.10);
  border: 1px solid rgba(255,253,247,0.25);
  backdrop-filter: blur(8px);
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: rgba(255,253,247,0.88);
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
  animation: ${fadeUp} 0.8s 0.7s ease both;
`;

const PrimaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 38px;
  background: #7C3A00;
  color: #FFFDF7;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  border-radius: 999px;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
  &:hover {
    background: #C07020;
    transform: scale(1.04) translateY(-1px);
    box-shadow: 0 12px 32px rgba(124,58,0,0.4);
  }
`;

const SecondaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 38px;
  background: rgba(255,253,247,0.08);
  backdrop-filter: blur(8px);
  color: #FFFDF7;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  border-radius: 999px;
  border: 1px solid rgba(255,253,247,0.28);
  transition: background 0.25s, transform 0.2s;
  &:hover {
    background: rgba(255,253,247,0.16);
    transform: scale(1.04) translateY(-1px);
  }
`;

const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  width: 48px; height: 48px;
  border-radius: 50%;
  background: rgba(255,253,247,0.10);
  border: 1px solid rgba(255,253,247,0.22);
  backdrop-filter: blur(8px);
  color: #FFFDF7;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.2s;
  ${({ $left }) => $left ? 'left: 24px;' : 'right: 24px;'}
  @media (min-width: 768px) {
    ${({ $left }) => $left ? 'left: 40px;' : 'right: 40px;'}
  }
  &:hover {
    background: rgba(124,58,0,0.6);
    transform: translateY(-50%) scale(1.08);
  }
`;

const Dots = styled.div`
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  gap: 10px;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '28px' : '8px')};
  height: 8px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#C07020' : 'rgba(255,253,247,0.35)')};
  transition: width 0.35s ease, background 0.3s;
`;

const ScrollArrow = styled.a`
  position: absolute;
  bottom: 36px;
  left: 50%;
  color: rgba(255,253,247,0.6);
  animation: ${bounceSubtle} 2.4s ease-in-out infinite;
  z-index: 20;
  transition: color 0.2s;
  &:hover { color: #FFFDF7; }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 2px;
  background: rgba(255,253,247,0.12);
  z-index: 20;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    background: #C07020;
    transform-origin: left center;
    animation: ${progressAnim} 6s linear both;
    animation-key: ${({ $key }) => $key};
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #7C3A00, #C07020);
  transform-origin: left center;
  animation: ${progressAnim} 6s linear both;
`;

/* ─── Component ───────────────────────────────────────────── */
export function Hero() {
  const { lang } = useLanguage();
  const { region } = useRegion();
  const [current, setCurrent] = useState(0);
  const [key, setKey] = useState(0);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    setKey((k) => k + 1);
  }, []);

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);

  useEffect(() => {
    const id = setTimeout(next, 6000);
    return () => clearTimeout(id);
  }, [current, next]);

  const slide = slides[current];
  const tx = (field) => slide[field][lang] ?? slide[field].en;

  return (
    <Section id="hero" aria-label="Hero – Hacienda Real">
      <SlideWrap key={key}>
        <BgImage src={slide.image} alt="" />
        <Overlay $grad={slide.overlay} />
        <GrainOverlay />
      </SlideWrap>

      <Content key={key}>
        <Eyebrow>{tx('eyebrow')}</Eyebrow>
        <Title>{tx('title')}</Title>
        <Subtitle>{tx('body')}</Subtitle>

        {slide.badge && <Badge>{slide.badge[lang] ?? slide.badge.en}</Badge>}

        {slide.stores && (
          <StoreRow>
            {slide.stores.map((s) => <StorePill key={s}>{s}</StorePill>)}
          </StoreRow>
        )}

        <ButtonRow>
          <PrimaryBtn to={`/${region}${slide.primary.to}`}>
            {slide.primary[lang] ?? slide.primary.en}
          </PrimaryBtn>
          <SecondaryBtn to={`/${region}${slide.secondary.to}`}>
            {slide.secondary[lang] ?? slide.secondary.en}
          </SecondaryBtn>
        </ButtonRow>
      </Content>

      <Dots>
        {slides.map((_, i) => (
          <Dot key={i} $active={i === current} onClick={() => goTo(i)} />
        ))}
      </Dots>

      {/* Progress bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: '2px', background: 'rgba(255,253,247,0.10)', zIndex: 20,
      }}>
        <ProgressFill key={key} />
      </div>

      <ScrollArrow href="#highlights" aria-label="Scroll down">
        <ChevronDown size={36} strokeWidth={1.3} />
      </ScrollArrow>
    </Section>
  );
}
